import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Inbox,
  Search,
  Filter,
  Mail,
  Phone,
  Calendar,
  ExternalLink,
  TrendingUp
} from 'lucide-react';
import { mockLeads } from '../mock/mockData';

const LeadRepository = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');

  const sources = ['all', 'LinkedIn', 'Website', 'Email Query', 'Meeting Booked', 'Mobile App'];

  const filteredLeads = mockLeads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.contact.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSource = selectedSource === 'all' || lead.source === selectedSource;
    return matchesSearch && matchesSource;
  });

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      qualified: 'bg-green-100 text-green-700',
      'meeting-scheduled': 'bg-purple-100 text-purple-700',
      converted: 'bg-emerald-100 text-emerald-700',
      lost: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-green-100 text-green-700'
    };
    return colors[priority] || 'bg-gray-100 text-gray-700';
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'LinkedIn':
        return <ExternalLink className="w-4 h-4" />;
      case 'Website':
        return <ExternalLink className="w-4 h-4" />;
      case 'Email Query':
        return <Mail className="w-4 h-4" />;
      case 'Meeting Booked':
        return <Calendar className="w-4 h-4" />;
      default:
        return <Inbox className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lead Repository</h1>
        <p className="text-gray-600">
          Centralized hub for all inbound leads from LinkedIn, website, apps, and meetings
        </p>
      </div>

      <div className="p-8 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Inbox className="w-8 h-8 text-blue-600" />
                <Badge variant="secondary">+28</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">342</p>
              <p className="text-sm text-gray-600 mt-1">Total Leads</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-8 h-8 text-green-600" />
                <Badge className="bg-green-100 text-green-700">+15%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">34%</p>
              <p className="text-sm text-gray-600 mt-1">Conversion Rate</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Calendar className="w-8 h-8 text-purple-600" />
                <Badge variant="secondary">This Week</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">47</p>
              <p className="text-sm text-gray-600 mt-1">Meetings Scheduled</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Mail className="w-8 h-8 text-orange-600" />
                <Badge className="bg-orange-100 text-orange-700">24h</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">23</p>
              <p className="text-sm text-gray-600 mt-1">Pending Follow-up</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
              <CardTitle>All Leads</CardTitle>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full sm:w-64"
                  />
                </div>
                <div className="flex space-x-2 overflow-x-auto">
                  {sources.map((source) => (
                    <Button
                      key={source}
                      size="sm"
                      variant={selectedSource === source ? 'default' : 'outline'}
                      onClick={() => setSelectedSource(source)}
                      className="whitespace-nowrap"
                    >
                      {source === 'all' ? 'All Sources' : source}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredLeads.map((lead) => (
                <Card
                  key={lead.id}
                  className="hover:shadow-md transition-all border-2 hover:border-blue-300"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{lead.name}</h3>
                          <Badge className={getStatusColor(lead.status)}>
                            {lead.status.replace('-', ' ')}
                          </Badge>
                          <Badge className={getPriorityColor(lead.priority)}>
                            {lead.priority}
                          </Badge>
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span className="font-medium">Contact:</span>
                            <span>{lead.contact}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{lead.email}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span className="font-medium">Service:</span>
                            <span>{lead.service}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end space-y-3">
                        <div className="flex items-center space-x-2">
                          {getSourceIcon(lead.source)}
                          <span className="text-sm font-medium text-gray-700">{lead.source}</span>
                        </div>
                        <span className="text-xs text-gray-500">{lead.date}</span>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">
                            <Phone className="w-3 h-3 mr-1" />
                            Call
                          </Button>
                          <Button size="sm" variant="outline">
                            <Mail className="w-3 h-3 mr-1" />
                            Email
                          </Button>
                          <Button size="sm">
                            <Calendar className="w-3 h-3 mr-1" />
                            Schedule
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredLeads.length === 0 && (
              <div className="text-center py-12">
                <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No leads found matching your criteria</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeadRepository;
